import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';
import { getEcolabelByValue } from '../../../config/ecolabels';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './EcolabelFilter.module.css';

const getQueryParamName = queryParamNames =>
  Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;

// Format URI component's query param: { pub_ecolabels: 'has_any:vegan,organic' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * A single ecolabel tile: the badge artwork above its name, toggling the option
 * in the multi-enum array the surrounding FilterForm submits.
 *
 * The native checkbox is kept in the DOM (visually hidden) rather than replaced
 * by a button, so the tiles stay keyboard operable and screen readers still
 * announce them as checkboxes.
 */
const EcolabelTile = props => {
  const { id, name, option, label, src } = props;

  return (
    <Field name={name} type="checkbox" value={option}>
      {({ input }) => {
        // Workaround for a Firefox + React Final Form bug, same as FieldCheckbox.
        // https://github.com/final-form/react-final-form/issues/134
        const handleChange = event => {
          input.onChange(event);
          input.onBlur(event);
        };

        return (
          <label
            htmlFor={id}
            className={classNames(css.tile, { [css.tileSelected]: input.checked })}
          >
            <input {...input} id={id} className={css.input} onChange={handleChange} />
            {src ? (
              <img className={css.badge} src={src} alt="" loading="lazy" />
            ) : (
              <span className={css.badgePlaceholder} />
            )}
            <span className={css.tileLabel}>{label}</span>
          </label>
        );
      }}
    </Field>
  );
};

// How many tiles the narrow browse-page filter column shows before "Show all".
// Three rows of three keeps Category and Price in view instead of pushing them
// a screen and a half down.
const COMPACT_VISIBLE_COUNT = 9;

const EcolabelTiles = props => {
  const { className, id, name, options, legend, isCompact, hiddenSelectionCount = 0 } = props;
  const intl = useIntl();
  const isCollapsible = isCompact && options.length > COMPACT_VISIBLE_COUNT;
  // Start expanded when the shopper arrives with a label ticked that would otherwise
  // be hidden — they should see what they're filtering by.
  const [isExpanded, setIsExpanded] = React.useState(hiddenSelectionCount > 0);
  const visibleOptions =
    isCollapsible && !isExpanded ? options.slice(0, COMPACT_VISIBLE_COUNT) : options;

  return (
    <fieldset className={classNames(css.fieldset, className)}>
      {legend ? <legend className={css.accessibilityLegend}>{legend}</legend> : null}
      <ul className={classNames(css.grid, { [css.gridCompact]: isCompact })}>
        {visibleOptions.map(({ option, label }) => {
          const ecolabel = getEcolabelByValue(option);
          const fieldId = `${id}.${option}`;
          return (
            <li key={fieldId} className={css.item}>
              <EcolabelTile
                id={fieldId}
                name={name}
                option={option}
                label={label || ecolabel?.label || option}
                src={ecolabel?.src}
              />
            </li>
          );
        })}
      </ul>
      {isCollapsible ? (
        <button
          type="button"
          className={css.showAllButton}
          onClick={() => setIsExpanded(expanded => !expanded)}
        >
          {isExpanded
            ? intl.formatMessage({ id: 'EcolabelFilter.showFewer' })
            : intl.formatMessage(
                { id: 'EcolabelFilter.showAll' },
                { count: options.length - COMPACT_VISIBLE_COUNT }
              )}
        </button>
      ) : null}
    </fieldset>
  );
};

/**
 * "Shop by ecolabel" as a search filter.
 *
 * Behaves exactly like SelectMultipleFilter (multi-select, has_any/has_all search
 * mode, live edit in the sidebar) — it just renders the ethy ecolabel artwork as a
 * grid of tiles instead of a list of checkboxes, so the browse page mirrors the
 * homepage's "Shop by ecolabel" row.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {string} props.id
 * @param {string} props.name
 * @param {node} props.label
 * @param {Function} props.getAriaLabel
 * @param {Array<string>} props.queryParamNames
 * @param {Object} props.initialValues
 * @param {Function} props.onSubmit
 * @param {Array<{option: string, label: string}>} props.options
 * @param {string} props.schemaType
 * @param {'has_all' | 'has_any'} props.searchMode
 * @param {boolean} [props.showAsPopup]
 * @param {boolean} [props.isDesktop] true in the browse page's narrow left filter column
 * @param {number} [props.contentPlacementOffset]
 * @returns {JSX.Element}
 */
const EcolabelFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    isDesktop,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_any:a,b,c" or "a,b,c"
  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        { count: selectedOptions.length }
      )
    : '';

  // Pass the initial values with the name key so that they reach the right field.
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  const isCompact = isDesktop && !showAsPopup;
  const hiddenSelectionCount = isCompact
    ? options.filter(
        ({ option }, index) =>
          index >= COMPACT_VISIBLE_COUNT && selectedOptions.includes(`${option}`)
      ).length
    : 0;

  const tiles = (
    <EcolabelTiles
      className={css.tiles}
      name={name}
      id={`${id}-ecolabel-group`}
      options={options}
      legend={label}
      // The desktop filter column is only ~240px wide and there are 15 ecolabels, so
      // there the tiles are smaller, three to a row, and the tail is behind "Show all".
      isCompact={isCompact}
      hiddenSelectionCount={hiddenSelectionCount}
    />
  );

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={labelForPopup}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      {tiles}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      {tiles}
    </FilterPlain>
  );
};

export default EcolabelFilter;
